/**
 * What happened to a release after it was cut.
 *
 * The releases views could say when something was released but never whether it
 * reached production, so a release that failed to deploy looked exactly like one
 * running happily. This joins the two.
 *
 * The join is on the **commit**, not on the version string. Every ArgoCD
 * Application here tracks `charts/<service>` on `main`, and semantic-release
 * commits the chart bump and tags it, so the revision ArgoCD deployed is the
 * commit the release points at. Observed on the entity page: deployed revision
 * `42629726` is exactly the commit of `v0.51.3`. Matching `v0.51.3` against a
 * `0.51.3` workload label would work too, but only for tags shaped like that one.
 */

export type EntityDeploymentState = {
  entityRef: string;
  syncStatus?: string;
  healthStatus?: string;
  liveVersion?: string;
  liveVersions?: string[];
  unhealthy?: boolean;
  history: { revision: string; status: string; deployedAt?: string }[];
  historyHorizon?: string;
};

/**
 * The parts of a release this join needs. No `entityRef`: the caller has
 * already picked the entity's state, and requiring it here would stop the
 * entity card passing its own rows, which carry no ref.
 */
export type ReleaseRef = {
  tagName: string;
  commitSha?: string;
  releasedAt: string;
};

export type ReleaseDeployment =
  /**
   * Deployed and still the running version. `outcomeUnknown` is set when the
   * source recorded the deployment but cannot say whether it finished, which is
   * the case for every CAP deployment that came through the legacy commit path.
   */
  | {
      status: 'live';
      deployedAt?: string;
      healthy: boolean;
      outcomeUnknown?: boolean;
    }
  /** Its deploy is in flight right now. */
  | { status: 'deploying'; deployedAt?: string }
  /** Deployed, and something newer has since replaced it. */
  | { status: 'superseded'; deployedAt?: string; supersededBy?: string }
  /** Its sync failed, so it never became the running version. */
  | { status: 'failed'; deployedAt?: string }
  /** Newer than the retained history and absent from it: never synced. */
  | { status: 'not-deployed' }
  /** Older than the retained history, so nothing can be said. */
  | { status: 'unknown' };

/**
 * Whether a release is live, was replaced, failed, or never arrived.
 *
 * `releases` is the entity's other releases, used only to name the successor of
 * a superseded one; a short revision is the fallback when no release matches the
 * commit that replaced it (a deploy of a commit that was never tagged).
 */
export function releaseDeployment(options: {
  release: ReleaseRef;
  state?: EntityDeploymentState;
  releases?: ReleaseRef[];
}): ReleaseDeployment | undefined {
  const { release, state } = options;
  if (!state) {
    // No cluster, or no source places this entity. Say nothing rather than
    // implying the release went nowhere.
    return undefined;
  }
  // Two sources, two kinds of revision. ArgoCD deploys a commit, so a release
  // matches by its commit; CAP deploys a tag, so it matches by name. Accepting
  // both means one join covers every deployable component rather than only the
  // CBWS services.
  const index = state.history.findIndex(
    entry =>
      (release.commitSha && entry.revision === release.commitSha) ||
      entry.revision === release.tagName,
  );

  if (index === -1 && !release.commitSha) {
    // Nothing to match on: no commit, and the tag is absent from the history.
    return { status: 'unknown' };
  }

  if (index === -1) {
    return outsideHistory(release, state)
      ? { status: 'unknown' }
      : { status: 'not-deployed' };
  }

  const entry = state.history[index];
  if (entry.status === 'failed' || entry.status === 'canceled') {
    return {
      status: 'failed',
      ...(entry.deployedAt && { deployedAt: entry.deployedAt }),
    };
  }

  // A deploy in flight is not yet the running version. Only the newest entry can
  // be in flight; an older one still reporting `running` is a stale row.
  if (
    index === 0 &&
    (entry.status === 'running' || entry.status === 'pending')
  ) {
    return {
      status: 'deploying',
      ...(entry.deployedAt && { deployedAt: entry.deployedAt }),
    };
  }

  // index 0 is the newest sync ArgoCD performed. Anything behind it has been
  // replaced, whatever its own outcome was.
  if (index > 0) {
    return {
      status: 'superseded',
      ...(entry.deployedAt && { deployedAt: entry.deployedAt }),
      ...(successorOf(index, state, options.releases) && {
        supersededBy: successorOf(index, state, options.releases),
      }),
    };
  }

  // The newest sync, but the cluster may still not be running it: a deploy can
  // succeed while a workload sits in CrashLoopBackOff on the new image, which is
  // exactly what compute-worker did. `healthy` carries that distinction rather
  // than pretending the release is fine.
  return {
    status: 'live',
    ...(entry.deployedAt && { deployedAt: entry.deployedAt }),
    healthy: state.unhealthy !== true && state.healthStatus !== 'Degraded',
    // The source has the deployment but not its outcome. Saying "live" flatly
    // would claim more than it knows.
    ...(entry.status === 'unknown' && { outcomeUnknown: true }),
  };
}

/**
 * True when the release predates everything ArgoCD still remembers.
 *
 * ArgoCD keeps about ten history entries, so most of a busy feed falls off the
 * end. Reporting those as "not deployed" would be wrong about nearly every older
 * release; `unknown` is the honest answer, and it is the same rule the CAP source
 * already applies to deployments it cannot judge.
 */
function outsideHistory(
  release: ReleaseRef,
  state: EntityDeploymentState,
): boolean {
  if (!state.historyHorizon) {
    return true;
  }
  return new Date(release.releasedAt) < new Date(state.historyHorizon);
}

/** The release that replaced this one, by name where possible. */
function successorOf(
  index: number,
  state: EntityDeploymentState,
  releases?: ReleaseRef[],
): string | undefined {
  // The entry that superseded it is the next newer one in the history.
  const successor = state.history[index - 1];
  if (!successor) {
    return undefined;
  }
  const tagged = releases?.find(
    candidate => candidate.commitSha === successor.revision,
  );
  return tagged ? tagged.tagName : successor.revision.slice(0, 8);
}

/** Human wording for a state, shared by the overview and the entity card. */
export function describeReleaseDeployment(
  deployment: ReleaseDeployment,
): string {
  switch (deployment.status) {
    case 'live':
      if (!deployment.healthy) {
        return 'live, unhealthy';
      }
      return deployment.outcomeUnknown ? 'live, outcome unknown' : 'live';
    case 'deploying':
      return 'deploying';
    case 'superseded':
      return deployment.supersededBy
        ? `replaced by ${deployment.supersededBy}`
        : 'replaced';
    case 'failed':
      return 'deploy failed';
    case 'not-deployed':
      return 'not deployed';
    default:
      return 'no deploy record';
  }
}
